/**
 * Suspire — Intelligence signals (Phase 0 of the Suspire Intelligence app)
 *
 * Publishes storefront signals as Shopify custom events (Shopify.analytics.publish)
 * for the Suspire app's Web Pixel to collect. Shopify's standard events (page_viewed,
 * product_viewed, collection_viewed, search_submitted, cart events) already reach
 * pixels natively, so they are NOT republished here. Publishing sends nothing by
 * itself: Shopify delivers custom events only to pixels, and each pixel runs only
 * with the consent it declares. GA4 / GTM are untouched (the dataLayer mirror only reads).
 *
 * Custom events (pixel side: analytics.subscribe('suspire:<name>', …); data in event.customData):
 *   suspire:visitor          — once per session per login state: visitor_id, logged_in
 *   suspire:page_engagement  — each time the page is hidden: active_seconds, max_scroll_pct,
 *                              page_type, path, product_id, seq (keep the highest seq per page)
 *   suspire:order_placed     — Pragma 1Checkout order completed: checkout_id, order_id,
 *                              transaction_id (1C_purchase_<checkoutId>), visitor_id
 *   suspire:<ga4 event>      — copy of our own GA4 dataLayer events: select_item, view_item_list,
 *                              view_cart, search, view_promotion, select_promotion, add_to_wishlist,
 *                              sign_up, generate_lead, whatsapp_click, video_start, coupon_used
 *
 * Identity: a first-party visitor id (cookie _sp_vid, rolling 1 year; Safari keeps
 * script-set cookies ~7 days) is also written to the cart as the hidden attribute _sp_vid
 * once the cart has items (never creates a cart, so page caching is unaffected). Pragma's
 * 1checkout.js carries cart attributes onto the Shopify order (verified 2026-10-19). Buy Now
 * opens 1checkout straight after its add, so it waits for window.suspireCartVidReady() first.
 * suspire:order_placed links the visitor to the order either way.
 *
 * Identify (dormant until window.suspireSignalsConfig.identifyUrl is set in theme.liquid): a
 * logged-in shopper's browser calls the app's App Proxy (/apps/suspire/identify?vid=<id>) once
 * per session. Shopify adds the signed logged_in_customer_id, so the backend can link this
 * visitor id to the customer; the page never sees or sends the customer id itself.
 *
 * Browser events are hints, never proof: anyone can publish custom events from the console.
 * Wrapped so it can never throw. Page context comes from window.suspireSignalsConfig (theme.liquid).
 */
(function () {
  'use strict';
  if (window.__suspireSignalsBound) return;
  window.__suspireSignalsBound = true;

  var cfg = window.suspireSignalsConfig || {};
  var VID = '_sp_vid';
  var ONE_CHECKOUT_HOST = 'https://pay.1checkout.ai';
  var MIRROR = {
    select_item: 1, view_item_list: 1, view_cart: 1, search: 1, view_promotion: 1,
    select_promotion: 1, add_to_wishlist: 1, sign_up: 1, generate_lead: 1,
    whatsapp_click: 1, video_start: 1, coupon_used: 1
  };

  function now() { return (window.Date && Date.now) ? Date.now() : (+new Date()); }
  function debounce(fn, ms) { var t; return function () { if (t) clearTimeout(t); t = setTimeout(fn, ms); }; }
  var onceMem = {};
  function once(key) {                      // true the first time per browser session
    try { if (sessionStorage.getItem(key)) return false; sessionStorage.setItem(key, '1'); return true; }
    catch (e) { if (onceMem[key]) return false; onceMem[key] = 1; return true; }
  }

  /* ------------- publish (queued until Shopify's pixel manager is ready) ------------- */
  var queue = [];
  function publisher() {
    var a = window.Shopify && window.Shopify.analytics;
    return (a && typeof a.publish === 'function') ? a : null;
  }
  function publish(name, data) {
    try {
      var a = publisher();
      if (a) { a.publish('suspire:' + name, data || {}); return; }
      if (queue.length < 50) queue.push([name, data]);
    } catch (e) { }
  }
  function flushWhenReady() {
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (publisher()) {
        clearInterval(iv);
        var q = queue; queue = [];
        for (var i = 0; i < q.length; i++) publish(q[i][0], q[i][1]);
      } else if (tries > 40) { clearInterval(iv); queue = []; }   // ~20 s: no pixel manager on this page
    }, 500);
  }

  /* ---------------- consent: follow Shopify's Customer Privacy API ---------------- */
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) { }
    return true;   // no privacy API = no consent banner configured (same as GA4 today)
  }
  function whenAllowed(start) {
    var started = false;
    function go() { if (!started && analyticsAllowed()) { started = true; start(); } }
    document.addEventListener('visitorConsentCollected', go);   // consent given later via the banner
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function' && !window.Shopify.customerPrivacy) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { go(); });
        setTimeout(go, 3000);   // the API never loaded: behave as today (no banner)
        return;
      }
    } catch (e) { }
    go();
  }

  /* ---------------- visitor id: cookie + hidden cart attribute ---------------- */
  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function newId() {
    try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) { }
    return 'v-' + now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }
  function visitorId() {
    var v = readCookie(VID);
    if (!/^[A-Za-z0-9-]{8,64}$/.test(v)) v = newId();
    var exp = new Date(now() + 365 * 864e5).toUTCString();     // rolling: refreshed on every page
    document.cookie = VID + '=' + encodeURIComponent(v) + '; expires=' + exp + '; path=/; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
    return v;
  }
  var cartVidWrite = null;   // the write in flight, shared by every caller
  function writeCartVid(v, attempt) {
    if (cfg.cartVid === v) return Promise.resolve();
    if (cartVidWrite) return cartVidWrite;
    attempt = attempt || 0;
    // pin-code-checker.js only blocks /cart/add (fetch) while a check is pending; wait it out anyway.
    if (window.pinValidationPending) {
      if (attempt < 5) setTimeout(function () { writeCartVid(v, attempt + 1); }, 3000);
      return Promise.resolve();
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      cartVidWrite = fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: { _sp_vid: v } })
      }).then(function (r) { if (r && r.ok) cfg.cartVid = v; })
        .catch(function () { })
        .then(function () { cartVidWrite = null; });
    } catch (e) { cartVidWrite = null; return Promise.resolve(); }
    return cartVidWrite;
  }
  // Buy Now opens 1checkout straight after /cart/add, before the observer below can write the
  // id, so those orders lost it (2026-10-19: 4 of 33 product-page checkouts carried _sp_vid,
  // 64 of 65 from the cart did). Buy Now waits for this first. Always resolves, at the latest
  // after maxMs, so checkout is never held up by it.
  var cartVidFor = '';   // set by init once consent allows the visitor id
  window.suspireCartVidReady = function (maxMs) {
    return new Promise(function (resolve) {
      if (!cartVidFor) { resolve(); return; }
      setTimeout(resolve, maxMs || 1500);
      writeCartVid(cartVidFor).then(resolve, resolve);
    });
  };
  function initCartVid(v) {
    cartVidFor = v;
    // theme.liquid renders the cart's current _sp_vid + item count, so most pages make no request.
    if (cfg.cartItems > 0) writeCartVid(v);
    // Any add-to-cart (theme form, S2 drawer, Selleasy, Boost quick add) hits /cart/add; watching
    // finished requests catches all of them without wrapping fetch / XHR again.
    try {
      if (!('PerformanceObserver' in window)) return;
      var po = new PerformanceObserver(function (list) {
        try {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (/\/cart\/add/.test(entries[i].name || '')) { writeCartVid(v); return; }
          }
        } catch (e) { }
      });
      po.observe({ type: 'resource', buffered: true });
    } catch (e) { }
  }

  /* ------------- identify: logged-in visitor → customer, via the App Proxy ------------- */
  function initIdentify(v) {
    if (!cfg.loggedIn || !cfg.identifyUrl || !once('sp_identify_' + v)) return;
    try {
      fetch(cfg.identifyUrl + '?vid=' + encodeURIComponent(v), { credentials: 'same-origin', cache: 'no-store' })
        .catch(function () { });
    } catch (e) { }
  }

  /* ---------------- page engagement: active time + scroll depth ---------------- */
  function initEngagement() {
    var IDLE_MS = 30000, TICK_MS = 5000;
    var active = 0, lastAct = now(), lastTick = now(), maxScroll = 0, seq = 0, lastSent = '';
    function act() { lastAct = now(); }
    function tick() {
      var t = now();
      if (document.visibilityState !== 'hidden' && t - lastAct < IDLE_MS) active += Math.min(t - lastTick, TICK_MS * 2);
      lastTick = t;
    }
    function scrollPct() {
      var doc = document.documentElement, body = document.body;
      var h = Math.max(doc.scrollHeight, body ? body.scrollHeight : 0) - window.innerHeight;
      var y = window.pageYOffset || doc.scrollTop || 0;
      return h > 0 ? Math.min(100, Math.round(y / h * 100)) : 100;
    }
    function trackScroll() { var p = scrollPct(); if (p > maxScroll) maxScroll = p; }
    ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel'].forEach(function (type) {
      window.addEventListener(type, act, { passive: true, capture: true });
    });
    window.addEventListener('scroll', debounce(trackScroll, 250), { passive: true });
    setInterval(tick, TICK_MS);
    function send() {
      tick(); trackScroll();
      var secs = Math.round(active / 1000);
      var sig = secs + '|' + maxScroll;
      if (sig === lastSent || secs < 1) return;
      lastSent = sig; seq++;
      publish('page_engagement', {
        page_type: cfg.pageType || undefined,
        path: location.pathname,
        product_id: cfg.productId || undefined,
        active_seconds: secs,
        max_scroll_pct: maxScroll,
        seq: seq
      });
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') send(); else { lastTick = now(); act(); }
    });
    window.addEventListener('pagehide', send);
  }

  /* ------------- mirror our own GA4 dataLayer events (read-only) ------------- */
  function mirror(entry) {
    try {
      if (!entry || typeof entry !== 'object' || !MIRROR[entry.event]) return;
      var data = {};
      for (var k in entry) {
        if (entry.hasOwnProperty(k) && k !== 'event' && k.indexOf('gtm') !== 0) data[k] = entry[k];
      }
      publish(entry.event, data);
    } catch (e) { }
  }
  function initMirror() {
    var dl = window.dataLayer = window.dataLayer || [];
    for (var i = 0; i < dl.length; i++) mirror(dl[i]);    // pushed before this file ran
    var prev = dl.push;
    dl.push = function () {
      var r = prev.apply(dl, arguments);                   // GTM / GA4 always run first, unchanged
      try { for (var j = 0; j < arguments.length; j++) mirror(arguments[j]); } catch (e) { }
      return r;
    };
  }

  /* ------------- Pragma 1Checkout order completed: link visitor → order ------------- */
  // Same postMessage contract as ga4-phase5-tracking.js (coupon_used) and 1checkout-cart-clear.
  // Publishes synchronously: the page may redirect straight after order_placed.
  function initOrderPlaced(vid) {
    window.addEventListener('message', function (event) {
      try {
        if (!event || event.origin !== ONE_CHECKOUT_HOST) return;
        var d = event.data;
        if (!d || d.message !== 'order_placed') return;
        var oc = window.oneCheckout || {};
        var checkoutId = d.checkoutId || oc.CHECKOUT_ID;
        var key = checkoutId || d.orderId;
        if (!key || !once('sp_order_' + key)) return;
        publish('order_placed', {
          checkout_id: checkoutId || undefined,
          order_id: d.orderId || undefined,
          transaction_id: checkoutId ? '1C_purchase_' + checkoutId : undefined,
          visitor_id: vid
        });
      } catch (e) { }
    }, false);
  }

  /* ---------------------------------- init ---------------------------------- */
  function init() {
    if (window.Shopify && window.Shopify.designMode) return;   // theme editor: no ids, no carts, no events
    whenAllowed(function () {
      try {
        var vid = visitorId();
        if (!publisher()) flushWhenReady();
        initMirror();
        initEngagement();
        initOrderPlaced(vid);
        if (once('sp_visitor_' + vid + (cfg.loggedIn ? '_in' : '_out'))) {
          publish('visitor', { visitor_id: vid, logged_in: !!cfg.loggedIn });
        }
        initCartVid(vid);
        initIdentify(vid);
      } catch (e) { }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
